import type Highcharts from 'highcharts';
import type { PointerEventObject } from 'highcharts';

import type { BlockThemeConfig, DChartInterface } from 'webapp-types';
import { GoalStatus } from 'webapp/enums/enums';
import { clamp, formatNumber } from 'webapp/utils/number';

import type VisualizationGauge from 'webapp/models-custom/visualization/gauge';
import type DChartComparePoint from 'webapp/d-chart/point/compare-point';
import ProgressOrGaugeBaseRenderer from 'webapp/d-chart/renderer/progress-or-gauge-base';
import type Series from 'webapp/d-chart/series/series';

// dataOptions as built here: its series' type comes from the chart options
type GaugeDataOptions = Omit<Highcharts.Options, 'series'> & {
	series: (Omit<Highcharts.SeriesGaugeOptions, 'type' | 'data'> & {
		data: DChartInterface.ChartDataPoint[];
	})[];
};

export default class GaugeRenderer extends ProgressOrGaugeBaseRenderer {
	gaugeShadowEl: HTMLElement | null = null;

	override get visualization(): VisualizationGauge {
		return super.visualization as VisualizationGauge;
	}

	get goalPoint(): DChartComparePoint | null {
		return this.visualization.goalPoint;
	}

	get goalStatus(): GoalStatus | null {
		return this.goalPoint?.goalData?.goalStatus || null;
	}

	// where the goal sits on the arc as a ratio between minValue and maxValue
	get goalAnglePosition(): number {
		const firstSeries = this.firstSeries;
		if (!firstSeries || !this.goalPoint) {
			return 0;
		}
		const { minPoint, maxPoint } = this.getPointsForSeries(firstSeries);
		if (!minPoint || !maxPoint) {
			return 0;
		}
		const { min, max } = this.getMinMaxValues(minPoint, maxPoint);
		const range = (max.value || 0) - (min.value || 0);
		if (range <= 0) {
			return 0;
		}
		return ((this.goalPoint.value || 0) - (min.value || 0)) / range;
	}

	override getValueProgressForSeries(series: Series) {
		const { primaryPoint, minPoint, maxPoint } = this.getPointsForSeries(series);
		const actualMaxPoint = this.goalPoint || maxPoint;
		if (primaryPoint) {
			return this.getValueProgressForPoints(primaryPoint, minPoint!, actualMaxPoint!);
		}
		return 0;
	}

	override get baseOptions(): Highcharts.Options {
		const that = this;
		let options = super.baseOptions;

		options = this.mergeOptions(options, {
			chart: {
				type: 'gauge',
				events: {
					redraw(this: Highcharts.Chart) {
						// faking inner shadow with second pane - also moving .highcharts-pane
						// element to .highcharts-plot-bands-0 after elements with gradients (showing shadow over gradients).
						let gaugeShadowEl = that.gaugeShadowEl;

						if (!gaugeShadowEl) {
							that.gaugeShadowEl = gaugeShadowEl =
								this.container.querySelector('.gauge-shadow');
						}

						if (gaugeShadowEl) {
							this.container
								.querySelector('.highcharts-plot-bands-0')
								?.appendChild(gaugeShadowEl);
						}
					},
					click: function (this: Highcharts.Chart, event: PointerEventObject) {
						const dsId = that.primaryPoint?.dsId;
						if (dsId) {
							that.sendEventForBlockContentClick({ dsId });
						}

						if (this.hoverPoint) {
							const { dbxPoint, dataLabel } = this
								.hoverPoint as DChartInterface.ChartHoverPoint;
							that.onPointClick(
								dbxPoint,
								event,
								dataLabel?.element?.firstChild as SVGElement,
							);
						}
					},
				},
			},
			pane: {
				startAngle: -90,
				endAngle: 90,
				background: [
					{
						innerRadius: '60%',
						outerRadius: '100%',
						shape: 'arc',
					},
					{
						innerRadius: '60%', // gaugeShadowEl
						outerRadius: '65%',
						shape: 'arc',
						className: 'gauge-shadow',
					},
				],
			},
			plotOptions: {
				gauge: {
					dial: {
						baseLength: '0%',
						radius: '102%',
						rearLength: '0%',
					},
				},
				series: {
					animation: {
						duration: this.dChart.isAnimated ? 500 : 0, // dial animation
					},
				},
			},
			yAxis: [
				{
					minorTicks: false,
					labels: {
						align: 'center', // out of arc - distance defined in sizeOptions
					},
				},
				{
					minorTicks: false,
					labels: {
						align: 'center',
						distance: '80%', // to center first and last label - percent between innerRadius and outerRadius of main pane -> (60% + 100%) / 2
					},
				},
			],
		});

		return options;
	}

	override get dataOptions(): Highcharts.Options {
		const firstSeries = this.firstSeries;

		if (!firstSeries) {
			return {};
		}

		const { primaryPoint, minPoint, maxPoint } = this.getPointsForSeries(firstSeries);

		if (!primaryPoint) {
			return {};
		}

		const { min, max } = this.getMinMaxValues(minPoint!, maxPoint!);

		const primaryPointValue = primaryPoint.value || 0,
			goalPointValue = this.goalPoint?.value || 0,
			minValue = min.value!,
			maxValue = max.value!;

		const { gradient1ClassName, gradient2ClassName } = this.stepGradientClassNames;

		// ----- setting plotBands and tickPositions ----
		let tickPositions: number[] = [],
			plotBands: Highcharts.YAxisPlotBandsOptions[] = [];
		{
			const thickness = '40%',
				staticClassName = 'step';

			if (primaryPointValue <= goalPointValue) {
				if (primaryPointValue > maxValue) {
					// min, max, primary, goal
					tickPositions = [minValue, maxValue];
					plotBands = [
						{
							from: minValue,
							to: maxValue,
							thickness,
							className: `${staticClassName} ${gradient1ClassName}`,
						},
					];
				} else if (primaryPointValue < minValue) {
					if (goalPointValue < maxValue && goalPointValue > minValue) {
						// primary, min, goal, max
						tickPositions = [minValue, goalPointValue, maxValue];
						plotBands = [
							{
								from: minValue,
								to: goalPointValue,
								thickness,
							},
							{
								from: goalPointValue,
								to: maxValue,
								thickness,
							},
						];
					} else {
						// primary, min, max, goal
						tickPositions = [minValue, maxValue];
						plotBands = [
							{
								from: minValue,
								to: maxValue,
								thickness,
							},
						];
					}
				} else {
					// primary is in range
					if (goalPointValue > maxValue) {
						// min, primary, max, goal
						tickPositions = [minValue, primaryPointValue, maxValue];
						plotBands = [
							{
								from: minValue,
								to: primaryPointValue,
								thickness,
								className: `${staticClassName} ${gradient1ClassName}`,
							},
							{
								from: primaryPointValue,
								to: maxValue,
								thickness,
							},
						];
					} else {
						// min, primary, goal, max
						tickPositions = [minValue, primaryPointValue, goalPointValue, maxValue];
						plotBands = [
							{
								from: minValue,
								to: primaryPointValue,
								thickness,
								className: `${staticClassName} ${gradient1ClassName}`,
							},
							{
								from: primaryPointValue,
								to: goalPointValue,
								thickness,
							},
							{
								from: goalPointValue,
								to: maxValue,
								thickness,
							},
						];
					}
				}
			} else if (primaryPointValue > goalPointValue) {
				if (goalPointValue > maxValue) {
					// min, max, goal, primary
					tickPositions = [minValue, maxValue];
					plotBands = [
						{
							from: minValue,
							to: maxValue,
							thickness,
							className: `${staticClassName} ${gradient1ClassName}`,
						},
					];
				} else if (primaryPointValue < minValue) {
					// goal, primary, min, max
					tickPositions = [minValue, maxValue];
					plotBands = [
						{
							from: minValue,
							to: maxValue,
							thickness,
						},
					];
				} else if (goalPointValue < minValue) {
					if (primaryPointValue > maxValue) {
						// goal, min, max, primary
						tickPositions = [minValue, maxValue];
						plotBands = [
							{
								from: minValue,
								to: maxValue,
								thickness,
								className: `${staticClassName} ${gradient2ClassName}`,
							},
						];
					} else {
						// goal, min, primary, max
						tickPositions = [minValue, primaryPointValue, maxValue];
						plotBands = [
							{
								from: minValue,
								to: primaryPointValue,
								thickness,
								className: `${staticClassName} ${gradient2ClassName}`,
							},
							{
								from: primaryPointValue,
								to: maxValue,
								thickness,
							},
						];
					}
				} else {
					// goal is in range
					if (primaryPointValue > maxValue) {
						// min, goal, max, primary
						tickPositions = [minValue, goalPointValue, maxValue];
						plotBands = [
							{
								from: minValue,
								to: goalPointValue,
								thickness,
								className: `${staticClassName} ${gradient1ClassName}`,
							},
							{
								from: goalPointValue,
								to: maxValue,
								thickness,
								className: `${staticClassName} ${gradient2ClassName}`,
							},
						];
					} else {
						// min, goal, primary, max
						tickPositions = [minValue, goalPointValue, primaryPointValue, maxValue];
						plotBands = [
							{
								from: minValue,
								to: goalPointValue,
								thickness,
								className: `${staticClassName} ${gradient1ClassName}`,
							},
							{
								from: goalPointValue,
								to: primaryPointValue,
								thickness,
								className: `${staticClassName} ${gradient2ClassName}`,
							},
							{
								from: primaryPointValue,
								to: maxValue,
								thickness,
							},
						];
					}
				}
			}
		}

		let plotLines: Highcharts.YAxisPlotLinesOptions[] = [];
		if (
			primaryPointValue !== goalPointValue &&
			goalPointValue > minValue &&
			goalPointValue < maxValue
		) {
			plotLines = [
				{
					value: goalPointValue,
					zIndex: 3,
				},
			];
		}

		const format = primaryPoint.format,
			scale = primaryPoint.scale;

		const formatter = (context: Highcharts.AxisLabelsFormatterContextObject): string => {
			let formattedValue = '';
			// show number label when different than current value but not if value is 0 (chart from 0 to max 1 - without data)
			const contextValue = context.value as number;
			if (contextValue !== primaryPointValue || contextValue === minValue) {
				formattedValue =
					contextValue > minValue
						? formatNumber(contextValue, format, { scale })
						: min.valueStr;
			}

			return formattedValue;
		};

		const percentValue = this.percent;

		const options: GaugeDataOptions = {
			plotOptions: {
				gauge: {
					dataLabels: {
						enabled: percentValue !== 0,
						formatter: function (): string {
							return `${percentValue}%`;
						},
						crop: false, // showing labels outside plot area
						// showing labels outside plot area; 'none' is a value Highcharts uses internally, missing in its types
						overflow: 'none' as string as Highcharts.DataLabelsOverflowValue,
					},
				},
			},
			series: [
				{
					name: primaryPoint.title,
					data: [
						{
							y: clamp(primaryPointValue, minValue, maxValue), // the indicator should not show outside of the min/max
							dbxPoint: primaryPoint,
						},
					],
				},
			],
			yAxis: [
				{
					labels: {
						formatter,
					},
					tickPositions,
					min: minValue,
					max: maxValue,
					plotBands,
					plotLines,
					showFirstLabel: false,
					showLastLabel: false,
				},
				{
					linkedTo: 0,
					tickPositions: [minValue, maxValue],
					tickLength: maxValue,
					labels: {
						formatter,
					},
				},
			],
		};

		// merging with sizeOptions because data options influence size (this.showPercent)
		return this.mergeOptions(this.sizeOptions, options);
	}

	override get sizeOptions(): Highcharts.Options {
		const m = Math.min(7, this.dChart.sizeModifier),
			sidePadding = 2.5 * m, // space we leave on left/right/top
			bottomLabelsHeight = Math.max(20, Math.ceil(2 * this.dChart.sizeModifier)); // space for percent bubble + min/max labels below gauge (no cap, scales with chart sizeModifier)

		// when the goal tick label lands near the top of the arc, reserve extra top space so it isn't clipped
		const goalAnglePos = this.goalAnglePosition;
		const isGoalLabelNearTop =
			goalAnglePos > 0 && goalAnglePos < 1 && Math.abs(goalAnglePos - 0.5) <= 0.1;
		const goalPointHeight = isGoalLabelNearTop
			? Math.max(11, Math.ceil(2 * this.dChart.sizeModifier))
			: 0;

		// how much space can we leave for the pane (gauge circle) itself
		const paneMaxWidth = this.dChart.maxWidth - 2 * sidePadding,
			paneMaxHeight =
				this.dChart.maxHeight - sidePadding - bottomLabelsHeight - goalPointHeight;

		const paneRadius = Math.min(paneMaxWidth / 2, paneMaxHeight);

		// calculate at what vertical % we'll put the pane center
		const extraPaddingTop = (this.dChart.maxHeight - paneMaxHeight) / 3, // extra free space on top (less on top than bottom, hence 3)
			paneVerticalPercentage = Math.round(
				((extraPaddingTop + paneRadius) / this.dChart.maxHeight) * 100,
			);

		let options = super.sizeOptions;
		options = this.mergeOptions(options, {
			plotOptions: {
				gauge: {
					dataLabels: {
						y: m,
						borderRadius: m,
						padding: m * 0.4,
					},
					dial: {
						baseWidth: m * 0.8,
						topWidth: m * 0.2,
						borderWidth: 0,
					},
					pivot: {
						radius: m * 0.32,
					},
				},
			},
			pane: {
				center: ['50%', `${paneVerticalPercentage}%`],
				size: 2 * paneRadius,
			},
			yAxis: [
				{
					labels: {
						padding: 0,
						x: 0,
						y: 0,
						distance: this.dChart.sizeModifier * 0.8,
					},
				},
				{
					labels: {
						padding: 0,
						x: 0,
						// adjust y position based on font size
						y: this.dChart.sizeModifier * 0.8,
					},
				},
			],
		});

		return options;
	}

	override updateSize(_hasModifierChanged: boolean): void {
		// update also when modifier is unchanged, only size changed
		super.updateSize(true);
	}

	get gradientDefs() {
		const themeData = this.dChart.themeData,
			themeId = themeData.themeId,
			colors = themeData.colors || [],
			trafficLightsColors = themeData.trafficLightsColors || [],
			baseColorIndex = themeData.visualizationConfig.base || 0,
			baseColor = colors[baseColorIndex];

		const gradientSteps =
				(themeData.visualizationConfig.steps as
					BlockThemeConfig.GradientStep[] | undefined) || [],
			stepLow = gradientSteps[0]!,
			stepMiddle = gradientSteps[gradientSteps.length - 2]!,
			stepHigh = gradientSteps[gradientSteps.length - 1]!;

		const trafficLightsGradientSteps = themeData.visualizationConfig.trafficLightsSteps || [];

		return [
			{
				// plot band gradients
				tagName: 'linearGradient',
				id: `gradient-gauge-plot-band-step-low-${themeId}`,
				x1: 0,
				y1: 0,
				x2: 0,
				y2: 1,
				children: [
					{
						tagName: 'stop',
						offset: stepLow.range[0],
						'stop-color': colors[stepLow.gradient[0]],
					},
					{
						tagName: 'stop',
						offset: stepLow.range[1],
						'stop-color': colors[stepLow.gradient[1]],
					},
				],
			},
			{
				tagName: 'linearGradient',
				id: `gradient-gauge-plot-band-step-middle-${themeId}`,
				x1: 0,
				y1: 0,
				x2: 0,
				y2: 1,
				children: [
					{
						tagName: 'stop',
						offset: stepMiddle.range[0],
						'stop-color': colors[stepMiddle.gradient[0]],
					},
					{
						tagName: 'stop',
						offset: stepMiddle.range[1],
						'stop-color': colors[stepMiddle.gradient[1]],
					},
				],
			},
			{
				tagName: 'linearGradient',
				id: `gradient-gauge-plot-band-step-high-${themeId}`,
				x1: 0,
				y1: 0,
				x2: 0,
				y2: 1,
				children: [
					{
						tagName: 'stop',
						offset: stepHigh.range[0],
						'stop-color': colors[stepHigh.gradient[0]],
					},
					{
						tagName: 'stop',
						offset: stepHigh.range[1],
						'stop-color': colors[stepHigh.gradient[1]],
					},
				],
			},
			...trafficLightsGradientSteps.map((step, index: number) => ({
				tagName: 'linearGradient',
				id: `gradient-gauge-plot-band-step-${['low', 'middle', 'high'][index]}-traffic-light-${themeId}`,
				x1: 0,
				y1: 0,
				x2: 0,
				y2: 1,
				children: [
					{
						tagName: 'stop',
						offset: 0,
						'stop-color': trafficLightsColors[step.gradient[0]],
					},
					{
						tagName: 'stop',
						offset: 1,
						'stop-color': trafficLightsColors[step.gradient[1]],
					},
				],
			})),
			{
				// pane background
				tagName: 'linearGradient',
				id: `background-gauge-pane-${themeId}-${baseColorIndex}`,
				x1: 0,
				y1: 0,
				x2: 0,
				y2: 1,
				children: [
					{
						tagName: 'stop',
						'stop-color': baseColor,
					},
				],
			},
		];
	}

	get stepGradientClassNames() {
		const stepClassNames = {
			LOW: 'step-low',
			MIDDLE: 'step-middle',
			HIGH: 'step-high',
			LOW_TRAFFIC_LIGHT: 'step-low-traffic-light',
			MIDDLE_TRAFFIC_LIGHT: 'step-middle-traffic-light',
			HIGH_TRAFFIC_LIGHT: 'step-high-traffic-light',
		};

		const isTrafficLightsEnabled = this.isTrafficLightsEnabled;
		const themeData = this.dChart.themeData;
		const gradientSteps =
			(isTrafficLightsEnabled
				? themeData.visualizationConfig.trafficLightsSteps
				: (themeData.visualizationConfig.steps as
						BlockThemeConfig.GradientStep[] | undefined)) || [];

		let gradient1ClassName = '',
			gradient2ClassName = '';

		const isInverted = this.visualization.isInverted;

		const setGradient2TrafficLightClassName = () => {
			const stepClassNameLow = stepClassNames.LOW_TRAFFIC_LIGHT,
				stepClassNameHigh = stepClassNames.HIGH_TRAFFIC_LIGHT;

			gradientSteps.some((step, index) => {
				if (step.range[0] <= valueRatio && step.range[1] > valueRatio) {
					gradient2ClassName = Object.values(stepClassNames)[index + 3] || '';
					return true;
				}
				return false;
			});

			if (!gradient2ClassName) {
				gradient2ClassName = stepClassNameHigh;
			}

			// check if inverted and swap gradient2ClassName
			if (isInverted) {
				if (gradient2ClassName === stepClassNameLow) {
					gradient2ClassName = stepClassNameHigh;
				} else if (gradient2ClassName === stepClassNameHigh) {
					gradient2ClassName = stepClassNameLow;
				}
			}
		};

		let valueRatio = this.percent / 100;
		if (this.goalPoint?.hasValue) {
			const goalStatus = this.goalStatus;
			if (!this.goalPoint.value) {
				valueRatio = 2;
			}

			if ((goalStatus && isTrafficLightsEnabled) || valueRatio >= 1) {
				if (goalStatus === GoalStatus.ON_TRACK || goalStatus === GoalStatus.ACHIEVED) {
					gradient2ClassName = stepClassNames.HIGH_TRAFFIC_LIGHT;
				} else if (goalStatus === GoalStatus.AT_RISK || goalStatus === GoalStatus.PARTIAL) {
					gradient2ClassName = stepClassNames.MIDDLE_TRAFFIC_LIGHT;
				} else {
					gradient2ClassName = stepClassNames.LOW_TRAFFIC_LIGHT;
				}
			} else {
				gradient2ClassName = stepClassNames.MIDDLE;
			}
		} else {
			if (isTrafficLightsEnabled) {
				setGradient2TrafficLightClassName();
			} else {
				// because https://app.asana.com/0/660520201167928/666170820059473
				if (isInverted) {
					gradient2ClassName = stepClassNames.LOW;
				} else {
					gradient2ClassName = stepClassNames.MIDDLE;
				}
			}
		}

		if (isTrafficLightsEnabled) {
			gradient1ClassName = gradient2ClassName;
		} else {
			gradient1ClassName = stepClassNames.MIDDLE;
		}

		return {
			gradient1ClassName,
			gradient2ClassName,
		};
	}
}
